import { useCallback, useEffect, useRef, useState } from "react";

const pickType = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";

export const canRecord = () => typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;

// Tap-to-start / tap-to-stop recorder with a time cap; onDone receives the audio Blob
export const useRecorder = (onDone, maxSeconds = 60) => {
  const [state, setState] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const rec = useRef(null);
  const timer = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const cleanup = () => { clearInterval(timer.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); };

  const stop = useCallback(() => { if (rec.current?.state === "recording") rec.current.stop(); }, []);

  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const type = pickType();
    const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const chunks = [];
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = () => {
      cleanup();
      setState("idle");
      doneRef.current(new Blob(chunks, { type: (r.mimeType || type || "audio/webm").split(";")[0] }));
    };
    rec.current = r;
    r.start();
    setSeconds(0);
    setState("recording");
    const t0 = Date.now();
    timer.current = setInterval(() => {
      const s = Math.floor((Date.now() - t0) / 1000);
      setSeconds(s);
      if (s >= maxSeconds) stop();
    }, 250);
  }, [stop, maxSeconds]);

  useEffect(() => () => { if (rec.current?.state === "recording") { rec.current.onstop = null; rec.current.stop(); } cleanup(); }, []);

  return { state, seconds, start, stop, max: maxSeconds };
};
